export type CoachKnowledgePassage = {
  sourceName: string;
  passageNumber: number;
  score: number;
  text: string;
};

export type CoachKnowledgeRetrievalOptions = {
  knowledgeSources: unknown;
  query: string;
  maxCharacters?: number;
  maxPassages?: number;
  maxPassagesPerSource?: number;
};

type KnowledgeChunk = Omit<CoachKnowledgePassage, 'score'> & {
  normalisedText: string;
  sourceOrder: number;
};

const DEFAULT_MAX_CHARACTERS = 6000;
const DEFAULT_MAX_PASSAGES = 6;
const MAX_CHUNK_CHARACTERS = 950;
const CHUNK_OVERLAP_CHARACTERS = 140;
const MAX_QUERY_TERMS = 80;
const MAX_PASSAGES_PER_SOURCE = 3;
export const RUNTIME_KNOWLEDGE_MAX_CHARACTERS = 2400;
export const RUNTIME_KNOWLEDGE_MAX_PASSAGES = 3;
export const RUNTIME_KNOWLEDGE_MAX_PASSAGES_PER_SOURCE = 2;
const RUNTIME_CONTEXT_HEADER = [
  'RUNTIME COACHING KNOWLEDGE:',
  'Use these current-coach source passages only when answering the learner\'s latest point. Do not mention retrieval or invent missing details.',
].join('\n\n');

const STOP_WORDS = new Set([
  'and', 'are', 'about', 'after', 'again', 'also', 'been', 'before', 'being', 'between', 'coach',
  'coaching', 'could', 'does', 'from', 'have', 'into', 'more', 'most', 'other',
  'for', 'session', 'should', 'the', 'their', 'there', 'these', 'they', 'this', 'those', 'through',
  'using', 'what', 'when', 'where', 'which', 'who', 'with', 'would', 'your',
]);

function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9%]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function queryTerms(query: string): string[] {
  return [...new Set(normalise(query).split(' '))]
    .filter((term) => term.length >= 3 && !STOP_WORDS.has(term))
    .slice(0, MAX_QUERY_TERMS);
}

function sourceText(source: Record<string, unknown>): string {
  return String(source.extractedText || source.extracted_text || source.content || '').trim();
}

function sourceName(source: Record<string, unknown>): string {
  return String(source.name || source.fileName || source.title || 'Supporting information').trim();
}

function splitIntoChunks(text: string): string[] {
  const paragraphs = text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const chunks: string[] = [];
  let current = '';

  const pushCurrent = () => {
    const clean = current.trim();
    if (clean) chunks.push(clean);
    current = '';
  };

  for (const paragraph of paragraphs) {
    if (paragraph.length > MAX_CHUNK_CHARACTERS) {
      pushCurrent();
      for (let start = 0; start < paragraph.length; start += MAX_CHUNK_CHARACTERS - CHUNK_OVERLAP_CHARACTERS) {
        chunks.push(paragraph.slice(start, start + MAX_CHUNK_CHARACTERS).trim());
      }
      continue;
    }

    const candidate = current ? `${current}\n${paragraph}` : paragraph;
    if (candidate.length <= MAX_CHUNK_CHARACTERS) {
      current = candidate;
      continue;
    }

    const overlap = current.slice(-CHUNK_OVERLAP_CHARACTERS).replace(/^\S*\s*/, '').trim();
    pushCurrent();
    current = overlap ? `${overlap}\n${paragraph}` : paragraph;
  }

  pushCurrent();
  return chunks;
}

function scoreChunk(chunk: KnowledgeChunk, terms: readonly string[]): number {
  const title = normalise(chunk.sourceName);
  const bodyTokens = new Set(chunk.normalisedText.split(' '));
  const titleTokens = new Set(title.split(' '));
  const matchedTerms = terms.filter((term) => bodyTokens.has(term));
  if (matchedTerms.length === 0) return 0;
  const titleMatches = terms.filter((term) => titleTokens.has(term)).length;
  const exactPhrase = terms.length >= 2 && chunk.normalisedText.includes(terms.slice(0, 5).join(' '));
  return matchedTerms.length * 3 + titleMatches * 5 + (exactPhrase ? 8 : 0);
}

export function buildCoachKnowledgeQuery(
  agent: Record<string, unknown>,
  template: Record<string, unknown> | null | undefined,
): string {
  const intakeScript = template?.intake_script && typeof template.intake_script === 'object'
    ? JSON.stringify(template.intake_script)
    : '';

  return [
    agent.name,
    agent.occupation,
    agent.roleplay_focus,
    agent.brief_description,
    agent.scenario_context,
    template?.name,
    template?.category,
    template?.system_prompt,
    intakeScript,
    agent.instructions,
  ].filter(Boolean).join('\n').slice(0, 18_000);
}

export function retrieveCoachKnowledge({
  knowledgeSources,
  query,
  maxCharacters = DEFAULT_MAX_CHARACTERS,
  maxPassages = DEFAULT_MAX_PASSAGES,
  maxPassagesPerSource = MAX_PASSAGES_PER_SOURCE,
}: CoachKnowledgeRetrievalOptions): CoachKnowledgePassage[] {
  if (
    !Array.isArray(knowledgeSources)
    || maxCharacters <= 0
    || maxPassages <= 0
    || maxPassagesPerSource <= 0
  ) return [];
  const terms = queryTerms(query);
  if (terms.length === 0) return [];

  const chunks = knowledgeSources.flatMap((source, sourceOrder): KnowledgeChunk[] => {
    if (!source || typeof source !== 'object') return [];
    const item = source as Record<string, unknown>;
    const text = sourceText(item);
    if (!text) return [];
    const name = sourceName(item);
    return splitIntoChunks(text).map((chunk, index) => ({
      sourceName: name,
      passageNumber: index + 1,
      text: chunk,
      normalisedText: normalise(chunk),
      sourceOrder,
    }));
  });

  const ranked = chunks
    .map((chunk) => ({ ...chunk, score: scoreChunk(chunk, terms) }))
    .filter((chunk) => chunk.score > 0)
    .sort((left, right) => (
      right.score - left.score
      || left.sourceOrder - right.sourceOrder
      || left.passageNumber - right.passageNumber
    ));

  const selected: CoachKnowledgePassage[] = [];
  const sourceCounts = new Map<string, number>();
  let usedCharacters = 0;

  for (const chunk of ranked) {
    if (selected.length >= maxPassages) break;
    const count = sourceCounts.get(chunk.sourceName) || 0;
    if (count >= maxPassagesPerSource) continue;

    const provenance = `[Source: ${chunk.sourceName}; passage ${chunk.passageNumber}]\n`;
    const separatorLength = selected.length > 0 ? 2 : 0;
    const remaining = maxCharacters - usedCharacters - separatorLength - provenance.length;
    if (remaining < 120) continue;
    const text = chunk.text.slice(0, remaining).trim();
    if (!text) continue;

    selected.push({
      sourceName: chunk.sourceName,
      passageNumber: chunk.passageNumber,
      score: chunk.score,
      text,
    });
    sourceCounts.set(chunk.sourceName, count + 1);
    usedCharacters += separatorLength + provenance.length + text.length;
  }

  return selected;
}

export function formatCoachKnowledgePassages(passages: readonly CoachKnowledgePassage[]): string[] {
  return passages.map((passage) => (
    `[Source: ${passage.sourceName}; passage ${passage.passageNumber}]\n${passage.text}`
  ));
}

export function coachKnowledgePassageKey(passage: CoachKnowledgePassage): string {
  return `${passage.sourceName}\u0000${passage.passageNumber}`;
}

export function retrieveRuntimeCoachKnowledge(
  knowledgeSources: unknown,
  learnerUtterance: string,
): CoachKnowledgePassage[] {
  return retrieveCoachKnowledge({
    knowledgeSources,
    query: learnerUtterance,
    maxCharacters: RUNTIME_KNOWLEDGE_MAX_CHARACTERS - RUNTIME_CONTEXT_HEADER.length - 2,
    maxPassages: RUNTIME_KNOWLEDGE_MAX_PASSAGES,
    maxPassagesPerSource: RUNTIME_KNOWLEDGE_MAX_PASSAGES_PER_SOURCE,
  });
}

export function formatRuntimeCoachKnowledgeContext(
  passages: readonly CoachKnowledgePassage[],
): string | null {
  if (passages.length === 0) return null;
  return [
    RUNTIME_CONTEXT_HEADER,
    ...formatCoachKnowledgePassages(passages),
  ].join('\n\n');
}
